import { MenuObjectTypes } from 'enums/menu';
import {
  IGetComboPlusQuery,
  IGetItemPlusQuery,
  IGetPickerPlusQuery,
  useGetComboPlusQuery,
  useGetItemPlusQuery,
  useGetPickerPlusQuery,
} from 'generated/sanity-graphql';
import { useGetVariantContentOptions } from 'pages/menu/hooks/use-get-variant-content-options';
import { withSimpleCache } from 'state/graphql/with-simple-cache';
import { IMenuObject } from 'state/menu/types';

import { getValidDataFromQuery } from './get-valid-data-from-query';

const useComboQuery = withSimpleCache(useGetComboPlusQuery);
const useItemQuery = withSimpleCache(useGetItemPlusQuery);
const usePickerQuery = withSimpleCache(useGetPickerPlusQuery);

export const useGetMenuObjectQuery = ({
  sanityId,
  menuObjectType,
  onCompleted,
}: {
  sanityId: string;
  menuObjectType: MenuObjectTypes;
  onCompleted?: (data: IMenuObject) => void;
}) => {
  const type = menuObjectType;

  const getVariantContentOptions = useGetVariantContentOptions();

  const getQueryOptions = (menuObjectType: MenuObjectTypes) => ({
    variables: { id: sanityId },
    skip: !sanityId || type !== menuObjectType,
    onCompleted: (data: IGetComboPlusQuery | IGetItemPlusQuery | IGetPickerPlusQuery) => {
      const menuObject = getValidDataFromQuery(data, getVariantContentOptions);
      if (menuObject && onCompleted) {
        onCompleted(menuObject);
      }
    },
  });

  const {
    data: comboData,
    error: comboError,
    loading: comboLoading,
    refetch: refetchCombo,
  } = useComboQuery(getQueryOptions(MenuObjectTypes.COMBO));
  const {
    data: itemData,
    error: itemError,
    loading: itemLoading,
    refetch: refetchItem,
  } = useItemQuery(getQueryOptions(MenuObjectTypes.ITEM));
  const {
    data: pickerData,
    error: pickerError,
    loading: pickerLoading,
    refetch: refetchPicker,
  } = usePickerQuery(getQueryOptions(MenuObjectTypes.PICKER));

  return {
    data: getValidDataFromQuery(comboData || itemData || pickerData, getVariantContentOptions),
    error: comboError || itemError || pickerError,
    loading: comboLoading || itemLoading || pickerLoading,
    refetch: refetchCombo || refetchItem || refetchPicker,
  };
};
